import axios, { AxiosError } from 'axios';
import qs from 'qs';

axios.defaults.headers.common['Content-Type'] = 'application/json;charset=UTF-8';

axios.defaults.paramsSerializer = (params) =>
    qs.stringify(params, { indices: false });

export const APISettings = {
    apiUrl: `${process.env.API_URL || 'http://localhost:9000'}/api/v1`,
    wsURL: `${process.env.API_URL || 'http://localhost:9000'}/api/ws/v1`,
    apiDateFormat: 'DDMMYYYY'
};

export interface TranslatedAxiosError {
    status: number | null;
    data: Record<string, unknown>;
    errorMessage?: string;
}

type LegacyCallback<T = unknown> = (err: TranslatedAxiosError | null, result?: T) => void;

export const translateAxiosError = (axiosError: AxiosError | Error): TranslatedAxiosError => {
    const errorObj: TranslatedAxiosError = {
        status: null,
        data: {},
        errorMessage: undefined
    };

    if (axios.isAxiosError(axiosError)) {
        if (axiosError.response) {
            errorObj.status = axiosError.response.status;
            errorObj.data = (axiosError.response.data as Record<string, unknown>) || {};
        } else if (axiosError.request) {
            errorObj.status = 0;
        } else {
            errorObj.errorMessage = axiosError.message;
        }
    } else {
        errorObj.errorMessage = axiosError.message;
    }

    return errorObj;
};

export default translateAxiosError;

export const doGet = <T = unknown>(url: string, params: Record<string, unknown>, callback: LegacyCallback<T>): void => {
    axios.get<T>(`${APISettings.apiUrl}${url}`, { params })
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const doGetAsync = async <T = unknown>(url: string, params: Record<string, unknown>): Promise<T> =>
    axios
        .get<T>(`${APISettings.apiUrl}${url}`, { params })
        .then((response) => response.data)
        .catch((error) => Promise.reject(translateAxiosError(error)));

export const doPost = <T = unknown>(
    url: string,
    data: unknown,
    requestParams: Record<string, unknown> | null,
    callback: LegacyCallback<T>
): void => {
    axios.post<T>(`${APISettings.apiUrl}${url}`, data, requestParams ? { params: requestParams } : {})
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const doPostAsync = async <T = unknown>(
    url: string,
    data: unknown,
    requestParams: Record<string, unknown> | null
): Promise<T> =>
    axios
        .post<T>(`${APISettings.apiUrl}${url}`, data, { params: requestParams ?? undefined })
        .then((response) => response.data)
        .catch((error) => Promise.reject(translateAxiosError(error)));

export const doPut = <T = unknown>(url: string, data: unknown, callback: LegacyCallback<T>): void => {
    axios.put<T>(`${APISettings.apiUrl}${url}`, data)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const doDelete = <T = unknown>(
    url: string,
    data: Record<string, unknown>,
    callback: LegacyCallback<T>
): void => {
    axios.delete<T>(`${APISettings.apiUrl}${url}`, data)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};
